import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const CATEGORIES = ["All", "Tactical Apparel", "Morale Patches & Coins", "Range Gear & Accessories", "Coffee & Provisions", "Faith Publications"];

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const { add } = useCart();

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => toast.error("Could not load the armory"))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(
    () => (category === "All" ? products : products.filter((p) => p.category === category)),
    [products, category]
  );

  return (
    <main className="pt-16" data-testid="store-page">
      <section className="relative border-b border-gold/15 overflow-hidden noise-overlay crosshair-grid">
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <p className="eyebrow mb-5">Club Store &amp; Armory</p>
          <MaskedLines
            lines={["PROVISIONS FOR", "THE ORDER."]}
            lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-parchment"
          />
          <p className="mt-6 max-w-xl text-stone-300 text-sm sm:text-base leading-relaxed">
            Every purchase funds range time, youth safety programs, and benevolence for member families.
            Member pricing applies automatically at checkout.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`font-condensed uppercase tracking-[0.15em] text-sm px-4 py-2 border transition-colors duration-300 ${
                category === c ? "bg-gold text-obsidian border-gold font-semibold" : "border-gold/25 text-stone-300 hover:border-gold hover:text-gold"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="store-loading">
            {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-card2/50 animate-pulse border border-gold/10" />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="py-24 text-center" data-testid="store-empty">
            <Package className="w-10 h-10 text-gold/50 mx-auto mb-4" />
            <p className="text-stone-400">Nothing in this category yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="product-grid">
            {visible.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.07}>
                <article className="group border border-gold/15 bg-card2/60 overflow-hidden flex flex-col h-full hover:border-crimson/40 transition-colors duration-500" data-testid={`product-card-${p.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-108 group-hover:scale-110 transition-transform duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                    <div className="absolute inset-3 border border-gold/0 group-hover:border-gold/40 transition-colors duration-500 pointer-events-none" />
                    {p.featured && (
                      <span className="absolute top-5 left-5 bg-crimson text-parchment font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 font-semibold shadow-[0_0_15px_rgba(178,34,34,0.4)]">Featured</span>
                    )}
                    <span className="absolute bottom-5 right-5 font-mono text-xs text-gold/90 bg-obsidian/70 px-2 py-1" data-testid={`product-stock-${p.id}`}>
                      {p.stock > 0 ? `${p.stock} IN STOCK` : "OUT OF STOCK"}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">{p.category} &mdash; {p.sku}</div>
                    <h2 className="font-condensed uppercase tracking-wider text-2xl text-parchment mt-1.5">{p.name}</h2>
                    <p className="text-sm text-stone-400 leading-relaxed mt-2.5 flex-1">{p.description}</p>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-mono text-xl text-gold" data-testid={`product-price-${p.id}`}>${p.price.toFixed(2)}</span>
                      <button
                        data-testid={`add-to-cart-${p.id}`}
                        onClick={() => { add(p); toast.success(`${p.name} added to your kit`); }}
                        className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-widest text-sm font-semibold px-5 py-2.5 hover:bg-gold-light hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-[background-color,box-shadow] duration-300"
                      >
                        Add to Kit
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
